import { AxiosError } from 'axios';
import {
  useMutation, UseMutationResult, useQuery, UseQueryResult,
} from 'react-query';
import {
  Account,
  AccountPlan,
  AccountRequest,
  AddCreditsRequest,
  AddUserSeatsRequest,
  CreateOrUpdateAccountPlanDto,
  GetCreditAllocationsResponse,
  GetCreditsResponse,
  UpdateAccountRequest,
} from '../@types/Account';
import {
  addCredits,
  addUserSeats,
  createAccount,
  createAccountPlan,
  deleteAccountPlan,
  getAccount,
  getCreditAllocations,
  getCredits,
  updateAccount,
  updateAccountPlan,
  uploadImageAccount,
} from '../api/accountsApi';
import { disconnectAtsIntegration } from '../api/atsApi';
import { getAssociatedLinkedinCompany } from '../api/companiesApi';

export const useCreateAccount = (
  onSuccessCallback?: () => void,
): UseMutationResult<Account, unknown, AccountRequest, unknown> => {
  return useMutation(
    async (account: AccountRequest) => createAccount(account),
    {
      onSuccess: () => onSuccessCallback?.(),
    },
  );
};
export const useUpdateAccount = (
  onSuccessCallback: () => void,
  id: string,
): UseMutationResult<unknown, AxiosError, UpdateAccountRequest, unknown> => {
  return useMutation(
    async (account: UpdateAccountRequest) => updateAccount(account, id),
    {
      onSuccess: () => onSuccessCallback(),
    },
  );
};

export const useGetAssociatedCompanyLinkedin = (
  onSuccessCallback?: (company: Company) => void,
): UseMutationResult<Company, AxiosError, string> => {
  return useMutation(
    (linkedinUrl) => {
      return getAssociatedLinkedinCompany(linkedinUrl);
    },
    {
      onSuccess: (company: Company) => {
        onSuccessCallback?.(company);
      },
      retry: 3,
    },
  );
};

export const useUploadAccountImage = (
  onErrorCallback: () => void,
): UseMutationResult<{ fileUrl: string }, unknown, FormData, unknown> => {
  return useMutation(
    (formData: FormData) => {
      return uploadImageAccount(formData);
    },
    {
      onError: () => {
        onErrorCallback();
      },
    },
  );
};

type UseAccountResponse = {
  account: Account | undefined;
  error: AxiosError | null;
  refetch: () => void;
  isLoading: boolean;
  isError: boolean;
};

export const useGetAccount = (accountId: string): UseAccountResponse => {
  const {
    data, refetch, error, isLoading, isError,
  } = useQuery<
    Account,
    AxiosError | null
  >(
    ['account-data', accountId],
    () => {
      return getAccount(accountId);
    },
    {
      enabled: !!accountId,
    },
  );

  return {
    account: data,
    error,
    refetch,
    isLoading,
    isError,
  };
};

export const useUpdateAccountPlan = (
  onSuccessCallback?: () => void,
): UseMutationResult<
  AccountPlan,
  AxiosError,
  {
    plan: Partial<CreateOrUpdateAccountPlanDto>;
    accountId: string;
    accountPlanId: string;
  }
> => {
  return useMutation(
    ['update-account'],
    ({ plan, accountId, accountPlanId }) => {
      return updateAccountPlan(accountId, accountPlanId, plan);
    },
    {
      onSuccess: onSuccessCallback,
    },
  );
};

export const useCreateAccountPlan = (
  onSuccessCallback?: () => void,
): UseMutationResult<
  AccountPlan,
  AxiosError,
  { accountId: string; plan: CreateOrUpdateAccountPlanDto }
> => {
  return useMutation(
    ['create-account'],
    ({ accountId, plan }) => {
      return createAccountPlan(accountId, plan);
    },
    {
      onSuccess: () => onSuccessCallback?.(),
    },
  );
};

export const useAddUserSeats = (
  accountId: string,
): UseMutationResult<void, AxiosError, AddUserSeatsRequest> => {
  return useMutation(
    ['add-user-seats', accountId],
    (payload: AddUserSeatsRequest) => addUserSeats(accountId, payload),
  );
};

export const useAddCredits = (
  accountId: string,
): UseMutationResult<void, AxiosError, AddCreditsRequest> => {
  return useMutation(['credits', accountId], (payload: AddCreditsRequest) => addCredits(accountId, payload),
  );
};

export const useGetCredits = (
  accountId: string,
): UseQueryResult<GetCreditsResponse, AxiosError> => {
  return useQuery(['credits', accountId], () => getCredits(accountId), {
    enabled: !!accountId,
  });
};

export const useDeleteAccountPlan = (
  accountId: string,
  onSuccessCallback: () => void,
): UseMutationResult<void, AxiosError, { accountPlanId: string }> => {
  return useMutation(
    ['delete-account-plan', accountId],
    ({ accountPlanId }) => {
      return deleteAccountPlan(accountId, accountPlanId);
    },
    {
      onSuccess: onSuccessCallback,
    },
  );
};

export const useGetCreditAllocations = (
  accountId: string,
): UseQueryResult<GetCreditAllocationsResponse, AxiosError> => {
  return useQuery<GetCreditAllocationsResponse, AxiosError | null>(
    ['credit-allocations'],
    () => getCreditAllocations(accountId),
  );
};

export const useDisconnectAtsIntegration = (onSuccessCallback?: () => void): UseMutationResult<void, AxiosError, string, unknown> => {
  return useMutation(
    (accountId: string) => disconnectAtsIntegration(accountId),
    { onSuccess: onSuccessCallback },
  );
};
